<script lang="ts">
	import { onDestroy, onMount, setContext } from 'svelte';
	import { writable } from 'svelte/store';
	import IconButton from '../buttons/IconButton.svelte';
	import type { TabContext, TabGroup } from '../../types/TabTypes';

	interface Props {
		//BIG THANKS TO https://svelte.dev/repl/8e68120858e5322272dc9136c4bb79cc?version=3.7.0
		tabGroups?: TabGroup[];
		tabParam?: any;
		top?: import('svelte').Snippet;
		tabContent?: import('svelte').Snippet;
	}
	//We use this information to set the starting tab.
	const urlParams = new URLSearchParams(window.location.search);

	let { tabGroups = [], tabParam = urlParams.get('tab'), top, tabContent }: Props = $props();

	const panels: any[] = [];
	const selectedTab = writable<string | null>(null);
	const selectedPanel: any | null = writable(null);

	function selectTab(tabName: string) {
		const panel = findPanel(tabName);

		if (!panel) {
			return;
		}

		selectedTab.set(tabName);
		selectedPanel.set(panel);

		const urlParams = new URLSearchParams(window.location.search);
		urlParams.set('tab', tabName);

		if (!panel.noQueryParam) {
			history.replaceState(null, '', window.location.pathname + '?' + urlParams);
		} else {
			history.replaceState(null, '', window.location.pathname);
		}

		const detailElement = document.getElementById('tab-container');
		detailElement?.classList.add('active');

		const navElement = document.getElementById('tab-navigation');
		navElement?.classList.remove('active');
	}

	function findPanel(tabName: string) {
		let indexIncrease = 0;

		for (let i = 0; i < tabGroups.length; i++) {
			const tabGroup = tabGroups[i];

			const index = tabGroup.tabs.findIndex((val, index, objects) => {
				return val == tabName;
			});

			if (index < 0) {
				indexIncrease += tabGroup.tabs.length;
				continue;
			}

			return panels[indexIncrease + index];
		}
	}

	if (tabGroups.length > 0) {
		selectedTab.update((current) => tabGroups[0].tabs[0]);
	}

	onMount(() => {
		if (tabParam && tabGroups.length > 0) {
			//Select the right tab
			selectTab(tabParam);
		}
	});

	setContext<TabContext>('tabContext', {
		registerPanel: (panel) => {
			panels.push(panel);
			selectedPanel.update((current: any) => current || panel);

			onDestroy(() => {
				const i = panels.indexOf(panel);
				panels.splice(i, 1);
				selectedPanel.update((current: any) =>
					current === panel ? panels[i] || panels[panels.length - 1] : current
				);
			});
		},
		selectTab,
		selectedTab,
		selectedPanel
	});
</script>

<div class="flex grow overflow-y-auto h-full">
	<div id="tab-navigation" class="flex flex-col overflow-y-auto pt-8 active">
		{@render top?.()}
		<div class="bg-background dark:bg-dark-200 rounded-md py-2">
			{#each tabGroups as tabGroup}
				<div class="py-4 pl-8 grid">
					<p class="mb-2 font-bold">{tabGroup.name}</p>

					{#each tabGroup.tabs as tab}
						<button
							class:selected={$selectedTab === tab}
							class="text-left border-l-4 border-transparent
                            relative py-2 pl-4 pr-4
                            hover:text-secondary-200 dark:hover:text-secondary-500
                            dark:selected:text-secondary-500 dark:selected:border-secondary-500
                            selected:text-secondary-200 selected:border-secondary-200 selected:font-bold"
							onclick={() => selectTab(tab)}
						>
							<p class="blur-none">
								{tab}
							</p>
						</button>
					{/each}
				</div>
			{/each}
		</div>
	</div>

	<div id="tab-container" class="grow overflow-y-auto flex flex-col">
		<div id="tab-nav-back-button" class="p-4">
			<IconButton
				iconName="chevron-left"
				onClick={() => {
					const detailElement = document.getElementById('tab-container');
					detailElement?.classList.remove('active');

					const navElement = document.getElementById('tab-navigation');
					navElement?.classList.add('active');
				}}
			></IconButton>
		</div>

		{@render tabContent?.()}
	</div>
</div>

<style>
	#tab-container {
		min-width: 75%;
	}

	#tab-navigation {
		min-width: 300px;
		max-width: 300px;
	}

	@media only screen and (min-width: 900px) {
		:global(#tab-nav-back-button) {
			display: none;
		}
	}

	@media only screen and (max-width: 900px) {
		#tab-container,
		#tab-navigation {
			width: 100%;
			max-width: none;
		}

		:global(#tab-container:not(.active)) {
			display: none;
		}

		:global(#tab-navigation:not(.active)) {
			display: none;
		}
	}
</style>
